// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { getTimeRecordAggregate } from '@/costs/api/get-time-record-aggregate';
import { TimeRecordFilter } from '@/costs/api/get-time-records';
import { TimeRecordAggregate } from '@/costs/api/types';
import { FIVE_MINUTES_MS, THIRTY_SECONDS_MS } from '@/utilities/time-constants';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

/**
 * Hook that fetches time record aggregate data for time records matching the provided filters
 * Uses the same filter interface as the time records endpoint but optimized for aggregation
 */
export function useTimeRecordAggregate(
  filter?: Omit<TimeRecordFilter, 'pageSize' | 'pageNumber' | 'sort'>
) {
  const queryResult = useQuery({
    queryKey: ['time-record-aggregate', filter],
    queryFn: () => getTimeRecordAggregate(filter),
    staleTime: THIRTY_SECONDS_MS,
    gcTime: FIVE_MINUTES_MS,
  });

  const aggregateData = useMemo<TimeRecordAggregate>(() => {
    if (!queryResult.data?.data) {
      // Return empty data structure when no data is available
      return {
        organizationCurrencyTotal: null,
        unratedEntries: 0,
        unconvertedEntries: 0,
        totalMinutes: 0,
        totalEntries: 0,
        byCurrency: [],
      };
    }

    return queryResult.data.data;
  }, [queryResult.data]);

  return {
    aggregateData,
    isLoading: queryResult.isLoading,
    isFetching: queryResult.isFetching,
    error: queryResult.error,
  } as const;
}
